<template>
  <!-- TKP-229: "Yeni veri" — eski veri ekleme ekranının alanları (Veri adı + Veri Kategorisi + Yeni kategori); veri kategorisiz açılmaz -->
  <b-modal :id="modalId" :title="$t('data.new')" no-close-on-backdrop data-test="new-data-dialog" @hidden="reset">
    <sweet-input :isValid="!$v.form.name.$error" @input="$v.form.name.$touch()" v-model="form.name" type="text" layout="vertical" :placeholder="$t('data.text')" :label="$t('data.text')" data-test="new-data-name" />

    <h6 class="mt-4">{{ $t("data-category.text") }}</h6>
    <filter-select id="newDataCategoryFilter" ref="categoryFilter" :placeholder="$t('data-category.text')" :multiple="false" @filter="onCategorySearch" :selected="form.category" @changed="onCategorySelect" data-test="new-data-category" />
    <small v-if="$v.form.category.$error" class="text-danger d-block" data-test="new-data-category-required">{{ $t("process-data.create-new.category-required") }}</small>

    <span v-if="!newCategoryOpen" class="text-primary hand small d-inline-block mt-2" data-test="new-data-new-category" @click="newCategoryOpen = true">+ {{ $t("data-category.new") }}</span>
    <div v-if="newCategoryOpen" class="border rounded p-2 mt-2" data-test="new-data-category-form">
      <CategoryForm :hide-library="true" @onSave="onCategoryCreated" />
    </div>

    <template #modal-footer="{ cancel }">
      <b-button variant="light" size="sm" @click="cancel()">{{ $t("common.cancel") }}</b-button>
      <b-button variant="success" size="sm" :disabled="saving || $v.$invalid" data-test="new-data-save" @click="save">
        <i class="bx" :class="saving ? 'bx-loader bx-spin' : 'bx-check'"></i> {{ $t("common.save") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { maxLength, required } from "vuelidate/lib/validators";
import CategoryForm from "@modules/data/organization-data-category-form.vue";
import { findSameName, mergeOrgAndLibrary, resolveNodeCategory, searchRequest } from "../unified-picker";
import { createDictionaryEntry } from "../new-dictionary-entry";

let seq = 0;

/**
 * TKP-229 — Varlıklar › veri ekleme panelindeki "+ Yeni veri ekle" ve aramadaki "➕ Yeni veri oluştur" bu pencereyi açar
 * (`ref.open(ad)`). Kontroller eski veri ekleme ekranıyla aynı: ad zorunlu (≤350), kategori zorunlu; aynı adlı veri sunucuda reddedilir.
 * Kayıt organizasyon sözlüğüne süreç bağlamıyla (süreç yöneticisi kendi sürecinde açabilir — TKP-200 m.7); bitince `created(veri)`.
 */
export default {
  name: "new-data-dialog",
  components: { CategoryForm },
  props: { process: { type: Object, default: null } },
  data() {
    return { uid: ++seq, saving: false, newCategoryOpen: false, form: { name: "", category: null } };
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(350), notBlank: (v) => !!String(v || "").trim() },
      category: { required },
    },
  },
  computed: {
    modalId() {
      return `new-data-${this.uid}`;
    },
  },
  methods: {
    open(name = "") {
      this.form = { name: String(name || "").trim(), category: null };
      this.newCategoryOpen = false;
      this.$v.$reset();
      this.$bvModal.show(this.modalId);
    },
    reset() {
      this.form = { name: "", category: null };
      this.newCategoryOpen = false;
      this.$v.$reset();
    },
    async onCategorySearch(value, callback) {
      const request = searchRequest(value, this.currentLanguage);
      const [org, library] = await Promise.all([
        this.$store.dispatch("organizationDataCategory/paginate", request),
        this.$store.dispatch("dataCategory/paginateForSearch", request).catch(() => ({ data: { docs: [] } })),
      ]);
      callback(mergeOrgAndLibrary(org.data?.docs ?? [], library.data?.docs ?? []));
    },
    onCategorySelect(item) {
      this.form = { ...this.form, category: item && item._id ? item : null };
      this.$v.form.category.$touch();
    },
    onCategoryCreated(category) {
      if (category && category._id) this.form = { ...this.form, category };
      this.newCategoryOpen = false;
    },
    async findExisting(text) {
      const request = searchRequest(String(text || "").trim(), this.currentLanguage);
      const [org, library] = await Promise.all([
        this.$store.dispatch("organizationData/paginateForSearch", request),
        this.$store.dispatch("data/paginateForSearch", request).catch(() => ({ data: { docs: [] } })),
      ]);
      return findSameName(mergeOrgAndLibrary(org.data?.docs ?? [], library.data?.docs ?? []), text, this.currentLanguage);
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$invalid || this.saving) return;
      this.saving = true;
      try {
        // TKP-230: ad organizasyon sözlüğünde ya da kütüphanede varsa yeni veri açılmaz — mevcut kayıt seçilir (mükerrer görünüm olmasın)
        const existing = await this.findExisting(this.form.name);
        if (existing) {
          this.$toast.info(this.$t("process-data.create-new.data-exists", { name: this.$options.filters.translate(existing.name) || this.form.name.trim() }));
          this.$emit("created", existing);
          this.$bvModal.hide(this.modalId);
          return;
        }
        const category = await resolveNodeCategory(this.$store.dispatch, this.form.category, this.process);
        const created = await createDictionaryEntry("/data", this.form.name.trim(), { category, process: this.process, language: this.currentLanguage });
        if (created && created._id) {
          this.$toast.success(this.$t("common.success"));
          this.$emit("created", created);
          this.$bvModal.hide(this.modalId);
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
